/**
 * Client-side greeting pool for new chats.
 * Selects a contextual greeting based on time gap since last message,
 * time of day, and rapid chat creation detection.
 */

interface PickGreetingOptions {
  lastMessageAt: Date | null;
  recentChatCount: number; // chats created in last 30s
  now?: Date;
}

type TimeOfDay = 'morning' | 'afternoon' | 'evening';

function getTimeOfDay(date: Date): TimeOfDay {
  const hour = date.getHours();
  if (hour < 12) return 'morning';
  if (hour < 17) return 'afternoon';
  return 'evening';
}

function formatTimeLabel(date: Date): string {
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const ampm = hours >= 12 ? 'pm' : 'am';
  const h = hours % 12 || 12;
  const m = minutes > 0 ? `:${String(minutes).padStart(2, '0')}` : '';
  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  return `${h}${m}${ampm} ${days[date.getDay()]}`;
}

// Rapid: user is opening lots of chats quickly — playful concern
const RAPID_GREETINGS = [
  'Everything alright? I\'m right here.',
  'My my, we are busy today.',
  'I do admire the enthusiasm.',
  'Still here! No need for a fresh one — but I shan\'t complain.',
  'Another one? I\'m flattered, truly.',
  'You rang? Again? At your service.',
];

// < 10 min: quick return — light, warm
const SHORT_GAP_GREETINGS = [
  'Fresh chat, fresh start. What\'s on your mind?',
  'Back so soon! I like the pace.',
  'New thread, same great team. Go ahead.',
  'Ah, a clean slate. Lovely. What shall we tackle?',
  'Right then — new chat. I\'m all ears.',
  'Quick turnaround! What are we thinking?',
];

// 10 min – 1 hr: casual return
const MEDIUM_GAP_GREETINGS = [
  'Nice little break? Ready when you are.',
  'And we\'re back. What\'s next on the agenda?',
  'Hope that went well. What can I help with?',
  'Recharged? Let\'s get into it.',
  'Right, where were we? Oh — new chat. Fresh topic then!',
  'Good timing. I just tidied up in here.',
];

// 1 hr – 6 hr: been a while
const LONG_GAP_GREETINGS = [
  'Welcome back! How can I help?',
  'There you are. Anything new to work on?',
  'Good to see you again. What\'s on your mind?',
  'Been a few hours — hope all is well. What can I do?',
  'Hello again! Ready for whatever you\'ve got.',
  'Ah, you\'re back. Splendid. What shall we sort out?',
];

// 6 hr+: long absence — time-of-day aware, warm
function makeLongAbsenceGreetings(timeOfDay: TimeOfDay, timeLabel: string): string[] {
  return [
    `Good ${timeOfDay}! It's ${timeLabel}. How can I help?`,
    `Good ${timeOfDay}. Lovely to see you — what's on the agenda?`,
    `It's ${timeLabel} and I'm at your disposal. What shall we do?`,
    `Good ${timeOfDay}! Hope you're well. Ready when you are.`,
    `Ah, good ${timeOfDay}. Fresh day, fresh possibilities. What's up?`,
    `Hello! It's ${timeLabel}. What can I help you with today?`,
  ];
}

function pick(pool: string[]): string {
  return pool[Math.floor(Math.random() * pool.length)];
}

export function pickGreeting(options: PickGreetingOptions): string {
  const now = options.now ?? new Date();
  const timeOfDay = getTimeOfDay(now);
  const timeLabel = formatTimeLabel(now);

  if (options.recentChatCount >= 2) {
    return pick(RAPID_GREETINGS);
  }

  if (!options.lastMessageAt) {
    // No previous messages — treat as long absence
    return pick(makeLongAbsenceGreetings(timeOfDay, timeLabel));
  }

  const gapMs = now.getTime() - options.lastMessageAt.getTime();
  const gapMinutes = gapMs / 60_000;

  if (gapMinutes < 10) {
    return pick(SHORT_GAP_GREETINGS);
  }
  if (gapMinutes < 60) {
    return pick(MEDIUM_GAP_GREETINGS);
  }
  if (gapMinutes < 360) {
    return pick(LONG_GAP_GREETINGS);
  }

  return pick(makeLongAbsenceGreetings(timeOfDay, timeLabel));
}

// ─── New Chat Placeholder Names ──────────────────────────────────────────────
// These are shown as the chat title until the user sends their first message,
// at which point the title is replaced with words from that message.

const CHAT_PLACEHOLDER_NAMES = [
  'Clean Slate',
  'Fresh Start',
  'Open Road',
  'Blank Canvas',
  'New Horizon',
  'Quiet Corner',
  'Morning Light',
  'Empty Page',
  'Clear Sky',
  'Open Field',
  'First Draft',
  'New Chapter',
  'Starting Line',
  'Wide Open',
  'Uncharted',
  'Bright Start',
  'Still Waters',
  'Open Air',
  'New Ground',
  'Plain Sight',
];

/** Pick a placeholder name that isn't already taken by an existing conversation. */
export function pickNewChatName(existingNames: Set<string>): string {
  const available = CHAT_PLACEHOLDER_NAMES.filter(
    (name) => !existingNames.has(name.toLowerCase()),
  );
  const pool = available.length > 0 ? available : CHAT_PLACEHOLDER_NAMES;
  const base = pool[Math.floor(Math.random() * pool.length)];

  // If the base name is taken, append incrementing suffix
  if (!existingNames.has(base.toLowerCase())) return base;
  let suffix = 2;
  while (existingNames.has(`${base} ${suffix}`.toLowerCase())) suffix++;
  return `${base} ${suffix}`;
}

/** Returns true if the name is still the auto-generated placeholder (not user/AI set). */
export function isNewChatPlaceholder(name: string | null | undefined): boolean {
  const normalized = String(name ?? '').trim().toLowerCase();
  if (normalized.length === 0) return true;
  // Legacy placeholder names
  if (/^(new chat|new conversation|welcome)(\s+\d+)?$/.test(normalized)) return true;
  // Current pool (strip optional numeric suffix)
  const base = normalized.replace(/\s+\d+$/, '');
  return CHAT_PLACEHOLDER_NAMES.some((n) => n.toLowerCase() === base);
}

// Track recent chat creations — persisted to localStorage so it survives page reloads (Capacitor).
const RECENT_CHATS_KEY = 'xavior:recentChatTimestamps';

function loadRecentTimestamps(): number[] {
  try {
    const raw = localStorage.getItem(RECENT_CHATS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveRecentTimestamps(timestamps: number[]): void {
  try {
    localStorage.setItem(RECENT_CHATS_KEY, JSON.stringify(timestamps));
  } catch { /* ignore */ }
}

export function recordChatCreation(): void {
  const timestamps = loadRecentTimestamps();
  timestamps.push(Date.now());
  saveRecentTimestamps(timestamps);
}

export function getRecentChatCount(windowMs = 30_000): number {
  const cutoff = Date.now() - windowMs;
  const timestamps = loadRecentTimestamps().filter(t => t >= cutoff);
  saveRecentTimestamps(timestamps);
  return timestamps.length;
}

const LAST_MESSAGE_KEY = 'xavior:lastMessageAt';

export function getLastMessageTimestamp(): Date | null {
  try {
    const stored = localStorage.getItem(LAST_MESSAGE_KEY);
    if (!stored) return null;
    const date = new Date(stored);
    return isNaN(date.getTime()) ? null : date;
  } catch {
    return null;
  }
}

export function setLastMessageTimestamp(date?: Date): void {
  try {
    localStorage.setItem(LAST_MESSAGE_KEY, (date ?? new Date()).toISOString());
  } catch {
    // localStorage may be unavailable
  }
}
